<template>
  <div :class="[is_pinned ? 'relative h-full' : 'flex h-full']">
    <div
      v-if="!mq.isMobile"
      class="w-[0.10rem] h-full hover:bg-primary-500 cursor-col-resize z-50 transition-colors duration-200"
      :class="{ 'bg-red-300': is_resizing, 'absolute left-0 top-0': is_pinned }"
      @mousedown="start_resize"
    />

    <div
      id="sidebar-notifications"
      class="bg-white h-full shadow-left z-40 shadow-lg flex"
      :class="[is_pinned ? '' : 'absolute top-0 right-0 z-50']"
      :style="{ width: mq.isMobile ? '100%' : sidebar_width + 'px' }"
    >
      <div
        v-if="!mq.isMobile"
        class="w-[0.10rem] h-full hover:bg-primary-500 cursor-col-resize z-50 transition-colors duration-200"
        :class="{ 'bg-red-300': is_resizing, 'absolute left-0 top-0': is_pinned }"
        @mousedown="start_resize"
      />
      <div class="flex flex-col w-full relative">
        <div class="flex items-center justify-between px-4 py-3">
          <div class="flex items-center gap-2">
            <div
              v-if="!mq.isMobile"
              v-tooltip.bottom="
                is_pinned ? $t('notification.labels.unpinSidebar') : $t('notification.labels.pinSidebar')
              "
              class="cursor-pointer size-8 flex items-center justify-center rounded-full hover:text-gray-800"
              :class="[is_pinned ? 'bg-gray-200 text-primary-500 ' : 'hover:bg-gray-200 text-gray-600 ']"
              @click="UPDATE_SIDEBAR_PREFS({ is_pinned: !is_pinned })"
            >
              <FontAwesomeIcon
                :icon="['far', 'thumbtack']"
                size="lg"
              />
            </div>
            <span class="font-semibold text-xl">
              {{ $t('notification.title') }}
            </span>
          </div>
          <div
            class="text-gray-500 hover:bg-gray-200 cursor-pointer size-8 flex items-center justify-center rounded-full hover:text-gray-800"
            data-test="sidebar-notifications-close"
            @click="SHOW_SIDEBAR(null)"
          >
            <FontAwesomeIcon
              :icon="['far', 'xmark']"
              size="lg"
            />
          </div>
        </div>

        <div
          v-if="fetch_notifications_at"
          class="absolute inset-0 bg-white/70 z-50 flex items-center justify-center"
        >
          <ProgressSpinner />
        </div>

        <NotificationsFilter
          class="shrink-0"
          :sort_order="sort_order"
          :sort_order_type="sort_order_type"
          @fetch_notification_subgroups="fetch_notification_subgroups"
          @open_notification_subgroups_all="open_notification_subgroups_all"
          @set_sort_order="
            (new_sort_prefs) => {
              sort_prefs = new_sort_prefs;
            }
          "
        />

        <section
          class="shrink-1 flex-1 grow overflow-y-auto"
          data-test="notification-container"
        >
          <div
            v-if="notification_subgroups_page.length === 0 && !fetch_notifications_at"
            class="text-center mt-5"
          >
            <p class="text-lg text-gray-500">
              <FontAwesomeIcon
                v-if="!is_filtering"
                class="mr-2"
                :icon="['far', 'thumbs-up']"
              />
              {{ $t(is_filtering ? 'notification.labels.noResults' : 'notification.labels.noNewNotification') }}
            </p>
          </div>
          <div
            v-for="(notification_subgroup, index) in notification_subgroups_page"
            :key="notification_subgroup.id"
            :class="{ 'border-b border-gray-100': index < notification_subgroups_page.length - 1 }"
          >
            <NotificationItem
              :notification_subgroup="notification_subgroup"
              @onChangeReadStatus="fetch_notification_subgroups"
              @onDelete="delete_notification_subgroup(notification_subgroup)"
              @click="handle_notification_click"
            />
          </div>
        </section>

        <div
          v-if="page_count > 1"
          class="p-2 shrink-0"
          @click.stop
        >
          <Paginator
            :rows="page_size"
            :totalRecords="notification_subgroup_pages_length"
            :first="(sidebar_prefs.page_number - 1) * page_size"
            @page="on_page_change"
            template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
            class="text-sm"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, inject, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import { NOTIFICATION_SUBGROUPS_PAGE_SIZE } from '@/app/data/pagination_constants';
import { PROFILE_TYPES } from '@/app/data/model_constants';
import NotificationsFilter from '@/app/shared_components/navbar/notifications/NotificationsFilter.vue';
import NotificationItem from '@/app/shared_components/activities/NotificationItem.vue';
import { differenceInSeconds } from 'date-fns';
import { useMqService } from '@/plugins/mq';
import { SIDEBAR_TYPE_NOTIFICATIONS } from '@/app/data/general_constants';

const SORT_ORDER_DEFAULT = 'desc';
const SORT_ORDER_TYPE_DEFAULT = 'updated_at';

const store = useStore();
const route = useRoute();
const mq = useMqService();
const emitter = inject('emitter');

const fetch_notifications_at = ref(null);
const page_size = ref(NOTIFICATION_SUBGROUPS_PAGE_SIZE);
const sort_prefs = ref([`${SORT_ORDER_TYPE_DEFAULT} ${SORT_ORDER_DEFAULT}`]);
const is_resizing = ref(false);
const width_min = 300;
const width_max = 800;
const x_start = ref(0);
const width_start = ref(0);

const notification_subgroup_pages_length = computed(() => store.getters.notification_subgroup_pages_length);
const notification_subgroups_page = computed(() => store.getters.notification_subgroups_page);
const sidebar_type = computed(() => store.getters.sidebar_type);
const sidebar_prefs = computed(() => store.getters.sidebar_prefs);

const is_filtering = computed(() => sidebar_prefs.value.project_id || sidebar_prefs.value.unopen);
const sidebar_width = computed(() => sidebar_prefs.value.sidebar_width);
const is_pinned = computed(() => {
  if (mq.isMobile) {
    return false;
  }
  return sidebar_prefs.value.is_pinned;
});
const page_count = computed(() => Math.ceil(notification_subgroup_pages_length.value / page_size.value));
const is_visible = computed(() => sidebar_type.value === SIDEBAR_TYPE_NOTIFICATIONS);
const sort_order = computed(() => _.last(sort_prefs.value).split(' ')[1]);
const sort_order_type = computed(() => _.last(sort_prefs.value).split(' ')[0]);

const DELETE_NOTIFICATION_SUBGROUP = (payload) => store.dispatch('DELETE_NOTIFICATION_SUBGROUP', payload);
const FETCH_NOTIFICATION_SUBGROUPS_PAGE = (payload) => store.dispatch('FETCH_NOTIFICATION_SUBGROUPS_PAGE', payload);
const OPEN_NOTIFICATION_SUBGROUPS = (payload) => store.dispatch('OPEN_NOTIFICATION_SUBGROUPS', payload);
const SHOW_SIDEBAR = (payload) => store.dispatch('SHOW_SIDEBAR', payload);
const UPDATE_NOTIFICATIONS_LAST_OPENED = () => store.dispatch('UPDATE_NOTIFICATIONS_LAST_OPENED');
const UPDATE_SIDEBAR_PREFS = (payload) => store.dispatch('UPDATE_SIDEBAR_PREFS', payload);

const fetch_notification_subgroups = async () => {
  if (!is_visible.value) return;
  if (fetch_notifications_at.value && differenceInSeconds(fetch_notifications_at.value, new Date()) > 60)
    fetch_notifications_at.value = null;
  if (fetch_notifications_at.value) return;

  fetch_notifications_at.value = new Date();
  page_size.value = _.max([Math.floor((window.innerHeight - 235) / 75), 10]);
  await FETCH_NOTIFICATION_SUBGROUPS_PAGE({ page_size: page_size.value });

  // catch current page being out of bounds: reset to first page and fetch again
  if (_.isEmpty(notification_subgroups_page.value) && sidebar_prefs.value.page_number > 1) {
    await UPDATE_SIDEBAR_PREFS({ page_number: 1 });
    await FETCH_NOTIFICATION_SUBGROUPS_PAGE({ page_size: page_size.value });
  }
  fetch_notifications_at.value = null;
};

const fetch_notification_subgroups_debounce = _.debounce(fetch_notification_subgroups, 1000);

const open_notification_subgroups_all = async () => {
  if (_.isEmpty(notification_subgroups_page.value)) return;
  const ids = _.map(notification_subgroups_page.value, 'id');
  await OPEN_NOTIFICATION_SUBGROUPS({ ids });
  await fetch_notification_subgroups();
};

const delete_notification_subgroup = (notification_subgroup) => {
  DELETE_NOTIFICATION_SUBGROUP({ notification_subgroup });
  fetch_notification_subgroups_debounce();
};

const handle_notification_click = () => {
  if (!is_pinned.value) {
    SHOW_SIDEBAR(null);
  }
};

const start_resize = (event) => {
  is_resizing.value = true;
  x_start.value = event.clientX;
  width_start.value = sidebar_width.value;
  event.preventDefault();
};

const handle_resize = (event) => {
  if (!is_resizing.value) return;

  const deltaX = event.clientX - x_start.value;
  const newWidth = width_start.value - deltaX;

  const new_sidebar_width = Math.max(width_min, Math.min(width_max, newWidth));
  UPDATE_SIDEBAR_PREFS({ sidebar_width: new_sidebar_width });
};

const stop_resize = () => {
  if (is_resizing.value) {
    is_resizing.value = false;
    UPDATE_SIDEBAR_PREFS({ sidebar_width: sidebar_width.value });
  }
};

const on_page_change = (event) => {
  UPDATE_SIDEBAR_PREFS({ page_number: event.page + 1 });
};

watch(
  () => route.path,
  () => {
    if (is_visible.value && !mq.isDesktop && !is_pinned.value) {
      SHOW_SIDEBAR(null);
    }
  },
  { immediate: true },
);

watch(is_visible, (val) => {
  if (val) {
    UPDATE_NOTIFICATIONS_LAST_OPENED();
    fetch_notification_subgroups();
  }
});

watch(
  () => sidebar_prefs.value.page_number,
  () => {
    fetch_notification_subgroups();
  },
);

watch(sort_prefs, () => {
  UPDATE_SIDEBAR_PREFS({ sort_prefs: sort_prefs.value });
  fetch_notification_subgroups();
});

onMounted(() => {
  if (sidebar_prefs.value.sort_prefs) {
    sort_prefs.value = sidebar_prefs.value.sort_prefs;
  } else {
    UPDATE_SIDEBAR_PREFS({ sort_prefs: sort_prefs.value });
  }
  if (emitter) {
    emitter.on('fetchNotificationSubgroups', fetch_notification_subgroups);
  }
  document.addEventListener('mousemove', handle_resize);
  document.addEventListener('mouseup', stop_resize);
});

onBeforeUnmount(() => {
  if (emitter) {
    emitter.off('fetchNotificationSubgroups', fetch_notification_subgroups);
  }
  document.removeEventListener('mousemove', handle_resize);
  document.removeEventListener('mouseup', stop_resize);
});
</script>
